import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Guid,
  isEmptyArray,
  isNil,
  isNotEmptyArray,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import { Button } from 'primeng/button';
import { ToggleSwitch } from 'primeng/toggleswitch';
import {
  SummernoteComponent,
  SummernoteImageUploadFn,
} from '@web/shared/external';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengSelectDirective,
  ValidationError,
  ValidationErrorParam,
} from '@web/shared/ui';
import { AdminBoxSaleGroupCosmeticsTableComponent } from '../../components/admin-box-sale-group-cosmetics-table/admin-box-sale-group-cosmetics-table.component';
import { AdminBoxSaleCosmeticAvailableQuantityModalComponent } from '../admin-box-sale-cosmetic-available-quantity-modal/admin-box-sale-cosmetic-available-quantity-modal.component';
import { AdminBoxSaleCosmeticSelectModalComponent } from '../admin-box-sale-cosmetic-select-modal/admin-box-sale-cosmetic-select-modal.component';
import {
  AdminBoxSaleDetailsForm,
  AdminBoxSaleGroupFormGroup,
  AdminBoxSaleGroupItem,
  AdminBoxSaleGroups,
  AdminBoxSaleGroupsFormGroup,
} from '../../models/admin-box-sale-details-form';
import { AdminBoxSaleGroupCosmetic } from '../../models/admin-box-sale-group-cosmetic';
import { AdminBoxSaleCosmeticAvailableQuantitySubmitEvent } from '../../events';
import { Select } from 'primeng/select';
import { AdminBoxSaleMissingItemPickStrategy } from '../../enums/admin-box-sale-missing-item-pick-strategy';
import { AdminBoxSaleMissingItemPickStrategyProviderService } from '../../providers/admin-box-sale-missing-item-pick-strategy-provider.service';
import { Tooltip } from 'primeng/tooltip';

@Component({
  selector: 'app-admin-box-sale-groups-control',
  imports: [
    Accordion,
    AccordionContent,
    AccordionHeader,
    AccordionPanel,
    AdminBoxSaleCosmeticAvailableQuantityModalComponent,
    AdminBoxSaleCosmeticSelectModalComponent,
    AdminBoxSaleGroupCosmeticsTableComponent,
    Button,
    CommonModule,
    FormControlComponent,
    PrimengButtonDirective,
    ReactiveFormsModule,
    SummernoteComponent,
    ToggleSwitch,
    PrimengSelectDirective,
    Select,
    Tooltip,
  ],
  templateUrl: './admin-box-sale-groups-control.component.html',
  styleUrl: './admin-box-sale-groups-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdminBoxSaleGroupsControlComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AdminBoxSaleGroupsControlComponent),
      multi: true,
    },
  ],
})
export class AdminBoxSaleGroupsControlComponent
  implements ControlValueAccessor, OnInit, Validator
{
  private readonly builder = inject(FormBuilder);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pickStrategyProvider = inject(AdminBoxSaleMissingItemPickStrategyProviderService);

  public readonly uploadFn = input.required<SummernoteImageUploadFn>();
  public readonly allowChangeGroups = input.required<boolean>();
  public readonly allowChangeCosmetics = input.required<boolean>();

  protected readonly form = this.buildForm();
  protected readonly isNotEmptyArray = isNotEmptyArray;
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly availableQuantityModalVisible = signal(false);
  protected readonly cosmeticModalVisible = signal(false);
  protected readonly editingCosmetic =
    signal<Nillable<AdminBoxSaleGroupCosmetic>>(null);
  protected readonly editingCosmeticGroupIndex = signal<Nillable<number>>(null);
  protected readonly editingGroupIndex = signal<Nillable<number>>(null);
  protected readonly strategySelectItems$ = this.pickStrategyProvider.selectItems();
  protected activeGroupIndex = -1;

  private onChange!: (value: Nillable<AdminBoxSaleGroups>) => void;
  private onTouched!: () => void;
  private onValidatorChange!: () => void;

  public ngOnInit(): void {
    this.listenToFormValueChanges();
    this.listenToFormStatusChanges();
  }

  public writeValue(value: Nillable<AdminBoxSaleGroups>): void {
    this.form.controls.groups.clear({ emitEvent: false });

    if (isNil(value)) {
      this.activeGroupIndex = -1;
      this.changeDetectorRef.detectChanges();

      return;
    }

    value.items.forEach((item) =>
      this.createAndPushGroup({
        emitEvent: false,
        initial: item,
      }),
    );

    this.updateActiveIndex();
  }

  public registerOnChange(
    fn: (value: Nillable<AdminBoxSaleGroups>) => void,
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.form.disable({ emitEvent: false })
      : this.form.enable({ emitEvent: false });
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    void control;

    if (this.form.controls.groups.length === 0) {
      return this.customValidationError(
        'Dodaj co najmniej jedną grupę zawartości.',
      );
    }

    if (this.hasGroupWithoutCosmetics()) {
      return this.customValidationError(
        'Każda grupa zawartości musi zawierać co najmniej jeden kosmetyk.',
      );
    }

    const groupWithoutAvailableQuantity =
      this.findFirstGroupIndexWithMissingAvailableQuantity();

    if (!isNil(groupWithoutAvailableQuantity)) {
      return this.customValidationError(
        `Wypełnienie dostępnej ilości kosmetyków w grupie "${groupWithoutAvailableQuantity + 1}" jest wymagane.`,
      );
    }

    if (this.form.invalid) {
      return this.customValidationError(
        'Jedna z grup zawartości zawiera nieprawidłowe dane.',
      );
    }

    return null;
  }

  public registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  protected groupsControls(): AdminBoxSaleGroupFormGroup[] {
    return this.form.controls.groups.controls;
  }

  protected handleAddClick(): void {
    this.createAndPushGroup({ emitEvent: true });
    this.updateActiveIndex();
    this.onTouched();
  }

  protected handleRemoveGroupClick(index: number): void {
    this.form.controls.groups.removeAt(index);
    this.updateActiveIndex();
    this.onTouched();
  }

  protected handleRemoveCosmeticClick(
    groupIndex: number,
    cosmeticId: Guid,
  ): void {
    this.updateGroupCosmetics(groupIndex, (cosmetics) =>
      cosmetics.filter((cosmetic) => cosmetic.id !== cosmeticId),
    );
  }

  protected handleEditCosmeticClick(
    groupIndex: number,
    cosmetic: AdminBoxSaleGroupCosmetic,
  ): void {
    this.editingCosmeticGroupIndex.set(groupIndex);
    this.editingCosmetic.set(cosmetic);
    this.availableQuantityModalVisible.set(true);
    this.onTouched();
  }

  protected handleSelectCosmeticsClick(groupIndex: number): void {
    this.editingGroupIndex.set(groupIndex);
    this.cosmeticModalVisible.set(true);
    this.onTouched();
  }

  protected handleAvailableQuantityModalCancelEvent(): void {
    this.availableQuantityModalVisible.set(false);
    this.editingCosmetic.set(null);
    this.editingCosmeticGroupIndex.set(null);
  }

  protected handleAvailableQuantityModalSubmitEvent(
    event: AdminBoxSaleCosmeticAvailableQuantitySubmitEvent,
  ): void {
    const groupIndex = this.editingCosmeticGroupIndex();
    const cosmetic = this.editingCosmetic();

    if (isNil(groupIndex) || isNil(cosmetic)) {
      return;
    }

    this.updateGroupCosmetics(groupIndex, (cosmetics) =>
      cosmetics.map((item) =>
        item.id === cosmetic.id
          ? item.withQuantities({
              amount: event.amount,
              availableQuantity: event.availableQuantity,
            })
          : item,
      ),
    );
    this.handleAvailableQuantityModalCancelEvent();
  }

  protected handleCosmeticModalCancelEvent(): void {
    this.cosmeticModalVisible.set(false);
    this.editingGroupIndex.set(null);
  }

  protected handleCosmeticModalSubmitEvent(
    items: {
      remainingQuantity?: Nillable<number>;
      cosmeticBrandName?: Nillable<string>;
      featuredFileAlt?: Nillable<string>;
      featuredFileUrl?: Nillable<string>;
      id: Guid;
      name: string;
    }[],
  ): void {
    const groupIndex = this.editingGroupIndex();

    if (isNil(groupIndex)) {
      return;
    }

    const control = this.form.controls.groups.at(groupIndex).controls.cosmetics;
    const current = valueOrDefault(control.value, []);
    const currentIds = new Set(current.map((item) => item.id));
    const next = [
      ...current,
      ...items
        .filter((item) => !currentIds.has(item.id))
        .map(
          (item) =>
            new AdminBoxSaleGroupCosmetic({
              amount: 1,
              availableQuantity: null,
              remainingQuantity: item.remainingQuantity,
              cosmeticBrandName: item.cosmeticBrandName,
              featureFileAlt: item.featuredFileAlt,
              featureFileUrl: item.featuredFileUrl,
              id: item.id,
              name: item.name,
            }),
        ),
    ];

    control.setValue(next);
    control.markAsDirty();
    control.updateValueAndValidity();
    this.handleCosmeticModalCancelEvent();
  }

  protected excludedIdsForEditingGroup(): Guid[] {
    const groupIndex = this.editingGroupIndex();

    if (isNil(groupIndex)) {
      return [];
    }

    return valueOrDefault(
      this.form.controls.groups.at(groupIndex).controls.cosmetics.value,
      [],
    ).map((item) => item.id);
  }

  protected cosmeticsFor(
    group: AdminBoxSaleGroupFormGroup,
  ): AdminBoxSaleGroupCosmetic[] {
    return valueOrDefault(group.controls.cosmetics.value, []);
  }

  private listenToFormValueChanges(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value) => {
        const groups = valueOrDefault(value.groups, []);

        if (this.form.invalid) {
          this.onChange(null);
          this.onTouched();

          return;
        }

        if (isEmptyArray(groups)) {
          this.onChange(null);
          this.onTouched();

          return;
        }

        this.onChange(
          new AdminBoxSaleGroups({
            items: groups.map(
              ({ cosmetics, description, id, isVisibleInQuestionnaire, missingItemPickStrategy }) => ({
                cosmetics: valueOrDefault(cosmetics, []),
                description,
                id,
                isVisibleInQuestionnaire: !!isVisibleInQuestionnaire,
                missingItemPickStrategy: valueOrDefault(missingItemPickStrategy, AdminBoxSaleMissingItemPickStrategy.MostAvailable),
              }),
            ),
          }),
        );
      },
    });
  }

  private listenToFormStatusChanges(): void {
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.notifyValidatorChange();
      });
  }

  private buildForm(): AdminBoxSaleGroupsFormGroup {
    return this.builder.group({
      groups: this.builder.array<AdminBoxSaleGroupFormGroup>([]),
    });
  }

  private createGroupFormGroup(): AdminBoxSaleGroupFormGroup {
    return this.builder.group({
      cosmetics: this.builder.nonNullable.control<AdminBoxSaleGroupCosmetic[]>(
        [],
        [this.cosmeticsValidator],
      ),
      description: this.builder.control<Nillable<string>>(null, [
        Validators.maxLength(AdminBoxSaleDetailsForm.DescriptionMaxLength),
      ]),
      id: this.builder.control<Nillable<Guid>>(null),
      isVisibleInQuestionnaire: this.builder.nonNullable.control<boolean>(true),
      missingItemPickStrategy: this.builder.nonNullable.control<AdminBoxSaleMissingItemPickStrategy>(AdminBoxSaleMissingItemPickStrategy.MostAvailable),
    });
  }

  private createAndPushGroup(
    options: {
      emitEvent?: boolean;
      initial?: AdminBoxSaleGroupItem;
    } = {},
  ): void {
    const { emitEvent = false, initial } = options;
    const formGroup = this.createGroupFormGroup();

    if (!isNil(initial)) {
      formGroup.patchValue({
        cosmetics: initial.cosmetics,
        description: initial.description,
        id: initial.id,
        isVisibleInQuestionnaire: initial.isVisibleInQuestionnaire,
        missingItemPickStrategy: initial.missingItemPickStrategy,
      });
    }

    this.form.controls.groups.push(formGroup, { emitEvent });
  }

  private updateActiveIndex(): void {
    const nextActiveGroupIndex = this.form.controls.groups.length - 1;

    this.activeGroupIndex = -1;
    this.changeDetectorRef.detectChanges();

    queueMicrotask(() => {
      this.activeGroupIndex = nextActiveGroupIndex;
      this.changeDetectorRef.detectChanges();
    });
  }

  private updateGroupCosmetics(
    groupIndex: number,
    factory: (
      cosmetics: AdminBoxSaleGroupCosmetic[],
    ) => AdminBoxSaleGroupCosmetic[],
  ): void {
    const control = this.form.controls.groups.at(groupIndex).controls.cosmetics;
    const cosmetics = factory(valueOrDefault(control.value, []));

    control.setValue(cosmetics);
    control.markAsDirty();
    control.updateValueAndValidity();
    this.onTouched();
  }

  private readonly cosmeticsValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const cosmetics = valueOrDefault(
      control.value as Nillable<AdminBoxSaleGroupCosmetic[]>,
      [],
    );

    return cosmetics.length >= 1
      ? null
      : {
          [ValidationError.ArrayMin]: {
            [ValidationErrorParam.Min]: 1,
          },
        };
  };

  private hasGroupWithoutCosmetics(): boolean {
    return this.form.controls.groups.controls.some(
      (group) => group.controls.cosmetics.invalid,
    );
  }

  private findFirstGroupIndexWithMissingAvailableQuantity(): Nillable<number> {
    const index = this.form.controls.groups.controls.findIndex((group) =>
      valueOrDefault(group.controls.cosmetics.value, []).some((cosmetic) =>
        isNil(cosmetic.availableQuantity),
      ),
    );

    return index === -1 ? null : index;
  }

  private notifyValidatorChange(): void {
    if (!isNil(this.onValidatorChange)) {
      this.onValidatorChange();
    }
  }

  private customValidationError(message: string): ValidationErrors {
    return {
      [ValidationError.Custom]: {
        [ValidationErrorParam.Message]: message,
      },
    };
  }
}
